import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Layout } from "@/components/Layout";
import { Logo } from "@/components/Logo";
import { Barbell, ChartLineUp, Heart, Trophy, Handshake, ArrowRight, CheckCircle, LockKey, Pill } from "@phosphor-icons/react";

const VALUE_ICONS = [
  { icon: Barbell, nameKey: "footer.value_discipline", descKey: "landing.value_discipline_desc", id: "discipline" },
  { icon: ChartLineUp, nameKey: "footer.value_results", descKey: "landing.value_results_desc", id: "results" },
  { icon: Heart, nameKey: "footer.value_health", descKey: "landing.value_health_desc", id: "health" },
  { icon: Trophy, nameKey: "footer.value_motivation", descKey: "landing.value_motivation_desc", id: "motivation" },
  { icon: Handshake, nameKey: "footer.value_confidence", descKey: "landing.value_confidence_desc", id: "confidence" },
];

const PLAN_FEATURE_KEYS = {
  start: ["start_1", "start_2", "start_3", "start_4"],
  evolution: ["evolution_1", "evolution_2", "evolution_3", "evolution_4", "evolution_5"],
  elite: ["elite_1", "elite_2", "elite_3", "elite_4", "elite_5", "elite_6"],
};

const STEP_KEYS = ["1", "2", "3", "4"];

export default function Landing() {
  const { t } = useTranslation();
  return (
    <Layout>
      {/* HERO */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden as-grain">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              'url("https://images.unsplash.com/photo-1605296867304-46d5465a13f1?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000")',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-as-black via-as-black/85 to-as-black/40" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl"
          >
            <p className="as-label text-as-green mb-6" data-testid="hero-tagline">
              {t("landing.hero_tagline")}
            </p>
            <h1 className="as-heading text-6xl sm:text-7xl md:text-8xl text-white mb-6">
              {t("landing.hero_title_1")}<br />
              {t("landing.hero_title_2")} <span className="text-as-green">{t("landing.hero_title_body")}</span><br />
              {t("landing.hero_title_3")} <span className="text-as-green">{t("landing.hero_title_life")}</span>
            </h1>
            <p className="text-lg text-as-gray max-w-xl mb-10 leading-relaxed">
              {t("landing.hero_subtitle")}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/evaluacion" className="as-btn-primary" data-testid="hero-cta-evaluation">
                {t("landing.hero_cta_evaluation")} <ArrowRight size={18} weight="bold" />
              </Link>
              <Link to="/planes" className="as-btn-secondary" data-testid="hero-cta-plans">
                {t("landing.hero_cta_plans")}
              </Link>
            </div>
          </motion.div>
        </div>
        <div className="absolute bottom-8 right-8 hidden lg:block z-10">
          <Logo size="md" showSlogan />
        </div>
      </section>

      {/* VALUES */}
      <section className="py-24 bg-as-black border-t border-as-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <p className="as-label text-as-green mb-3">{t("landing.values_label")}</p>
            <h2 className="as-heading text-5xl sm:text-6xl text-white max-w-2xl">
              {t("landing.values_title_1")} <span className="text-as-green">{t("landing.values_title_2")}</span>
            </h2>
            <div className="as-divider mt-6" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-as-border">
            {VALUE_ICONS.map((v, i) => (
              <motion.div
                key={v.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="bg-as-black p-8 group hover:bg-as-surface transition-colors"
                data-testid={`value-${v.id}`}
              >
                <v.icon size={40} weight="duotone" color="#39FF14" className="mb-5" aria-hidden="true" />
                <h3 className="as-heading text-xl text-white mb-2 tracking-wider uppercase">{t(v.nameKey)}</h3>
                <p className="text-sm text-as-gray">{t(v.descKey)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 bg-as-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="as-label text-as-green mb-3">{t("landing.how_label")}</p>
          <h2 className="as-heading text-5xl sm:text-6xl text-white mb-12">
            {t("landing.how_title_1")} <span className="text-as-green">{t("landing.how_title_2")}</span>
          </h2>
          <div className="grid md:grid-cols-4 gap-6">
            {STEP_KEYS.map((n) => (
              <div key={n} className="as-card" data-testid={`step-0${n}`}>
                <p className="as-heading text-5xl text-as-green mb-4" aria-hidden="true">0{n}</p>
                <h3 className="as-heading text-xl text-white mb-2">
                  <span className="sr-only">{t("landing.how_step_prefix")} {n}: </span>
                  {t(`landing.how_step_${n}_title`)}
                </h3>
                <p className="text-sm text-as-gray">{t(`landing.how_step_${n}_desc`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EXERCISE PREVIEW */}
      <section className="py-24 bg-as-black border-t border-as-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="as-label text-as-green mb-3">{t("landing.exercises_label")}</p>
            <h2 className="as-heading text-4xl sm:text-5xl text-white">
              {t("landing.exercises_title_1")} <span className="text-as-green">{t("landing.exercises_title_2")}</span>
            </h2>
            <p className="text-as-gray mt-4 max-w-2xl mx-auto">
              {t("landing.exercises_desc_1")} <span className="text-white font-bold">{t("landing.exercises_desc_count")}</span> {t("landing.exercises_desc_2")}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto mb-10">
            <ExerciseTeaser
              nameKey="landing.exercise_squat_barbell"
              muscleKey="landing.muscle_legs"
              testId="teaser-legs"
              poster="https://customer-assets.emergentagent.com/job_as-training/artifacts/9d9064pd_file_00000000fc4871f5973c6a792b85f260.png"
            />
            <ExerciseTeaser
              nameKey="landing.exercise_military_press"
              muscleKey="landing.muscle_shoulders"
              testId="teaser-shoulders"
              poster="https://customer-assets.emergentagent.com/job_as-training/artifacts/2rlehnrv_Pressmilitar.png"
            />
            <ExerciseTeaser
              nameKey="landing.exercise_biceps_barbell"
              muscleKey="landing.muscle_biceps"
              testId="teaser-biceps"
              poster="https://customer-assets.emergentagent.com/job_as-training/artifacts/n8zqs842_file_00000000ed00722f8361b95b207081c1.png"
            />
          </div>
          <div className="text-center">
            <Link to="/planes" className="as-btn-primary inline-flex items-center gap-2" data-testid="exercises-preview-cta">
              {t("landing.exercises_cta")} <ArrowRight size={16} weight="bold" />
            </Link>
            <p className="text-xs text-as-gray mt-3 uppercase tracking-widest">{t("landing.exercises_access_note")}</p>
          </div>
        </div>
      </section>

      {/* SUPPLEMENTS PREVIEW */}
      <section className="py-24 bg-as-surface border-t border-as-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="as-label text-as-green mb-3">{t("landing.supplements_label")}</p>
            <h2 className="as-heading text-4xl sm:text-5xl text-white">
              {t("landing.supplements_title_1")} <span className="text-as-green">{t("landing.supplements_title_2")}</span>
            </h2>
            <p className="text-as-gray mt-4 max-w-2xl mx-auto">
              <span className="text-white font-bold">{t("landing.supplements_desc_count")}</span> {t("landing.supplements_desc")}
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-5 max-w-4xl mx-auto mb-10">
            <SupplementTeaser nameKey="landing.supplement_whey" categoryKey="landing.supplement_cat_protein" testId="sup-teaser-protein" />
            <SupplementTeaser nameKey="landing.supplement_creatine" categoryKey="landing.supplement_cat_performance" testId="sup-teaser-performance-1" />
            <SupplementTeaser nameKey="landing.supplement_caffeine" categoryKey="landing.supplement_cat_performance" testId="sup-teaser-performance-2" />
          </div>
          <div className="text-center">
            <Link to="/planes" className="as-btn-primary inline-flex items-center gap-2" data-testid="supplements-preview-cta">
              {t("landing.supplements_cta")} <ArrowRight size={16} weight="bold" />
            </Link>
            <p className="text-xs text-as-gray mt-3 uppercase tracking-widest">{t("landing.supplements_access_note")}</p>
          </div>
        </div>
      </section>

      {/* PLANS PREVIEW */}
      <section className="py-24 bg-as-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="as-label text-as-green mb-3">{t("landing.plans_label")}</p>
            <h2 className="as-heading text-5xl sm:text-6xl text-white">
              {t("landing.plans_title_1")} <span className="text-as-green">{t("landing.plans_title_2")}</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <PlanCard id="start" name="AS START" price={49} featureKeys={PLAN_FEATURE_KEYS.start} />
            <PlanCard id="evolution" name="AS EVOLUTION" price={99} featureKeys={PLAN_FEATURE_KEYS.evolution} popular />
            <PlanCard id="elite" name="AS ELITE" price={199} featureKeys={PLAN_FEATURE_KEYS.elite} />
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-32 overflow-hidden as-grain">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              'url("https://images.unsplash.com/photo-1544021601-3e5723f9d333?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000")',
          }}
        />
        <div className="absolute inset-0 bg-as-black/85" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="as-heading text-5xl sm:text-7xl text-white mb-6">
            {t("landing.cta_final_title_1")} <span className="text-as-green">{t("landing.cta_final_title_2")}</span>
          </h2>
          <p className="text-lg text-as-gray mb-10 max-w-xl mx-auto">
            {t("landing.cta_final_subtitle")}
          </p>
          <Link to="/evaluacion" className="as-btn-primary" data-testid="final-cta-button">
            {t("landing.cta_final_button")} <ArrowRight size={20} weight="bold" />
          </Link>
          <div className="mt-6">
            <Link to="/historias" className="as-label text-as-green hover:text-white transition-colors" data-testid="landing-stories-link">
              {t("landing.stories_link")}
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}

const PlanCard = ({ id, name, price, featureKeys, popular }) => {
  const { t } = useTranslation();
  return (
    <div
      className={
        popular
          ? "bg-as-black border-2 border-as-green p-8 flex flex-col relative glow-green"
          : "bg-as-surface border border-as-border p-8 flex flex-col hover:border-as-green transition-colors"
      }
      data-testid={`plan-card-${id}`}
    >
      {popular && (
        <span className="absolute -top-3 left-8 bg-as-green text-as-black px-3 py-1 as-label text-[10px]">
          {t("landing.plan_popular")}
        </span>
      )}
      <h3 className="as-heading text-2xl text-white mb-2">{name}</h3>
      <p className="as-heading text-5xl text-as-green mb-1" aria-label={t("landing.plan_price_aria", { price })}>${price}</p>
      <p className="as-label mb-6" aria-hidden="true">{t("landing.plan_price_per_month")}</p>
      <ul className="space-y-3 mb-8 flex-1">
        {featureKeys.map((k) => (
          <li key={k} className="flex items-start gap-2 text-sm text-white">
            <CheckCircle size={18} weight="fill" color="#39FF14" className="flex-shrink-0 mt-0.5" aria-hidden="true" />
            <span>{t(`plan_features.${k}`)}</span>
          </li>
        ))}
      </ul>
      <Link
        to={`/planes`}
        className={popular ? "as-btn-primary w-full" : "as-btn-secondary w-full"}
        data-testid={`plan-cta-${id}`}
      >
        {t("landing.plan_choose")}
      </Link>
    </div>
  );
};

const ExerciseTeaser = ({ nameKey, muscleKey, poster, testId }) => {
  const { t } = useTranslation();
  const name = t(nameKey);
  const muscle = t(muscleKey);
  return (
    <Link
      to="/planes"
      className="group block bg-as-surface border border-as-border hover:border-as-green transition-colors relative overflow-hidden"
      data-testid={testId}
    >
      <div className="aspect-square relative overflow-hidden">
        <img
          src={poster}
          alt={name}
          className="w-full h-full object-cover transition-all duration-500 group-hover:scale-105"
          style={{ filter: "blur(2px) brightness(0.7)" }}
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-as-black/90" />
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center px-4">
          <div className="w-14 h-14 rounded-full bg-as-black/80 backdrop-blur-sm flex items-center justify-center border border-as-green/40 mb-2">
            <LockKey size={22} weight="duotone" color="#39FF14" />
          </div>
          <p className="as-label text-as-green text-[10px] tracking-widest">{t("landing.premium_content")}</p>
          <p className="text-white font-bold text-xs uppercase">{t("landing.unlock_with_start")}</p>
        </div>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-10">
          <p className="text-white text-3xl font-black tracking-widest -rotate-12 select-none">
            AS TRAINING
          </p>
        </div>
      </div>
      <div className="p-4 border-t border-as-border">
        <p className="as-label text-as-green text-[10px] mb-1">{muscle}</p>
        <h3 className="as-heading text-base text-white leading-tight">{name.toUpperCase()}</h3>
        <p className="text-xs text-as-gray mt-2 uppercase tracking-widest group-hover:text-as-green transition-colors">
          {t("landing.view_full_card")} →
        </p>
      </div>
    </Link>
  );
};

const SupplementTeaser = ({ nameKey, categoryKey, testId }) => {
  const { t } = useTranslation();
  const name = t(nameKey);
  const category = t(categoryKey);
  return (
    <Link
      to="/planes"
      className="group block bg-as-black border border-as-border hover:border-as-green transition-colors relative overflow-hidden"
      data-testid={testId}
    >
      <div className="p-6 relative">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="w-12 h-12 rounded-full bg-as-surface flex items-center justify-center border border-as-green/30">
            <Pill size={22} weight="duotone" color="#39FF14" />
          </div>
          <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 bg-as-green text-as-black">
            {t("landing.supplement_evidence")} {t("landing.supplement_evidence_high")}
          </span>
        </div>
        <p className="as-label text-as-green text-[10px] mb-1">{category}</p>
        <h3 className="as-heading text-lg text-white mb-3 leading-tight">{name.toUpperCase()}</h3>
        <div className="relative overflow-hidden mb-4" style={{ height: "60px" }}>
          <p className="text-xs text-as-gray leading-relaxed" style={{ filter: "blur(4px)", userSelect: "none" }}>
            Beneficios, cómo funciona, dosis recomendada, cuándo tomarlo, efectos secundarios y quién debería usarlo o evitarlo.
          </p>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-as-black/60 to-as-black" />
        </div>
        <div className="flex items-center justify-between pt-3 border-t border-as-border">
          <div className="flex items-center gap-1 text-as-gray text-[10px] uppercase tracking-widest">
            <LockKey size={12} weight="bold" color="#39FF14" /> {t("landing.supplement_full_card")}
          </div>
          <span className="text-as-green text-[10px] uppercase tracking-widest group-hover:underline">{t("landing.unlock")} →</span>
        </div>
      </div>
    </Link>
  );
};
